import React, { useEffect, useState } from 'react';
import { Card, CardBody, CardTitle, Badge, Button, Row, Col, Input, Spinner, Alert } from 'reactstrap';
import { Link, useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { setBreadcrumbItems } from '../../store/actions';
import api from '../../utils/api';

const actionLabels = { create: 'إنشاء', update: 'تعديل', delete: 'حذف' };
const entityLabels = { journalist: 'صحفي', activist: 'ناشط', institution: 'مؤسسة' };
const statusLabels = {
  pending: { text: 'معلّق', color: 'warning' },
  approved: { text: 'موافق عليه', color: 'success' },
  rejected: { text: 'مرفوض', color: 'danger' },
  partially_approved: { text: 'موافقة جزئية', color: 'info' },
};

const PendingApprovals = (props) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ status: 'pending', entity_type: '', action: '' });
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });

  useEffect(() => {
    props.setBreadcrumbItems('طلبات الموافقة', [
      { title: 'الرئيسية', link: '/dashboard' },
      { title: 'طلبات الموافقة', link: '#' },
    ]);
  }, []);

  useEffect(() => {
    fetchItems();
  }, [filters]);

  const fetchItems = async (page = 1) => {
    setLoading(true);
    setError('');
    try {
      const params = { ...filters, page };
      const res = await api.get('/pending-changes', { params });
      setItems(res.data.data || []);
      setPagination({
        current_page: res.data.current_page,
        last_page: res.data.last_page,
        total: res.data.total,
      });
    } catch (err) {
      setError('فشل في جلب البيانات');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    const day = d.getDate();
    const month = d.getMonth() + 1;
    const year = d.getFullYear();
    const hours = d.getHours().toString().padStart(2, '0');
    const minutes = d.getMinutes().toString().padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  return (
    <div>
      <Row className="mb-3">
        <Col md={3}>
          <Input type="select" value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
            <option value="pending">المعلّقة</option>
            <option value="all">الكل</option>
            <option value="approved">الموافق عليها</option>
            <option value="rejected">المرفوضة</option>
            <option value="partially_approved">موافقة جزئية</option>
          </Input>
        </Col>
        <Col md={3}>
          <Input type="select" value={filters.entity_type} onChange={e => setFilters(f => ({ ...f, entity_type: e.target.value }))}>
            <option value="">كل الكيانات</option>
            <option value="journalist">صحفيين</option>
            <option value="activist">ناشطين</option>
            <option value="institution">مؤسسات</option>
          </Input>
        </Col>
        <Col md={3}>
          <Input type="select" value={filters.action} onChange={e => setFilters(f => ({ ...f, action: e.target.value }))}>
            <option value="">كل العمليات</option>
            <option value="create">إنشاء</option>
            <option value="update">تعديل</option>
            <option value="delete">حذف</option>
          </Input>
        </Col>
        <Col md={3} className="text-end">
          <Badge color="warning" className="p-2 font-size-14">
            {pagination.total} طلب
          </Badge>
        </Col>
      </Row>

      {error && <Alert color="danger">{error}</Alert>}

      {loading ? (
        <div className="text-center py-5"><Spinner color="primary" /><p className="mt-2">جار التحميل...</p></div>
      ) : items.length === 0 ? (
        <Card><CardBody className="text-center py-5">
          <i className="mdi mdi-check-circle-outline mdi-48px text-success mb-3 d-block"></i>
          <h5>لا توجد طلبات {filters.status === 'pending' ? 'معلّقة' : ''}</h5>
        </CardBody></Card>
      ) : (
        <>
          {items.map(item => (
            <Card key={item.id} className="mb-3 border-start border-4" style={{ borderColor: statusLabels[item.status]?.color === 'warning' ? '#ffc107' : statusLabels[item.status]?.color === 'success' ? '#28a745' : statusLabels[item.status]?.color === 'danger' ? '#dc3545' : '#17a2b8' }}>
              <CardBody>
                <Row className="align-items-center">
                  <Col md={6}>
                    <div className="d-flex align-items-center mb-2">
                      <Badge color={item.action === 'create' ? 'success' : item.action === 'update' ? 'info' : 'danger'} className="me-2">
                        {actionLabels[item.action] || item.action}
                      </Badge>
                      <Badge color="primary" className="me-2">
                        {entityLabels[item.entity_type] || item.entity_type}
                      </Badge>
                      <Badge color={statusLabels[item.status]?.color || 'secondary'}>
                        {statusLabels[item.status]?.text || item.status}
                      </Badge>
                    </div>
                    <h5 className="mb-1">
                      {item.new_data?.full_name || item.new_data?.title || item.original_data?.full_name || item.original_data?.title || '—'}
                    </h5>
                    <small className="text-muted">
                      <i className="mdi mdi-account me-1"></i>
                      {item.user?.name || 'مستخدم غير معروف'}
                      <span className="mx-2">|</span>
                      <i className="mdi mdi-clock-outline me-1"></i>
                      {formatDate(item.created_at)}
                    </small>
                    {item.action === 'update' && item.changed_fields && (
                      <div className="mt-1">
                        <small className="text-muted">
                          <i className="mdi mdi-pencil me-1"></i>
                          {item.changed_fields.length} حقل متغير
                        </small>
                      </div>
                    )}
                  </Col>
                  <Col md={6} className="text-end">
                    <Button
                      color="primary"
                      size="sm"
                      onClick={() => navigate(`/dashboard/pending-approvals/${item.id}`)}
                    >
                      <i className="mdi mdi-eye me-1"></i>
                      مراجعة
                    </Button>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          ))}

          {/* Pagination */}
          {pagination.last_page > 1 && (
            <div className="d-flex justify-content-center mt-3">
              {Array.from({ length: pagination.last_page }, (_, i) => i + 1).map(page => (
                <Button
                  key={page}
                  color={page === pagination.current_page ? 'primary' : 'light'}
                  size="sm"
                  className="mx-1"
                  onClick={() => fetchItems(page)}
                >
                  {page}
                </Button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default connect(null, { setBreadcrumbItems })(PendingApprovals);
